import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Settings, RefreshCw, Upload, Download, Check, X, AlertTriangle,
  Database, ArrowLeftRight, Clock, FileSpreadsheet, History, 
  CheckCircle, XCircle, Loader2, Eye, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  'Sucesso': 'bg-green-500',
  'Parcial': 'bg-amber-500',
  'Falha': 'bg-red-500',
  'Em_Progresso': 'bg-blue-500'
};

export default function SendysIntegracaoPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  
  // Form state
  const [endpoint, setEndpoint] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [frequencia, setFrequencia] = useState('Manual');
  const [horaSync, setHoraSync] = useState('02:00');
  const [autoSync, setAutoSync] = useState(false);
  const [regraConflito, setRegraConflito] = useState('Mais_Recente');
  const [syncDadosBase, setSyncDadosBase] = useState(true);
  const [syncValencias, setSyncValencias] = useState(true);
  const [syncUtentes, setSyncUtentes] = useState(true);
  const [syncContactos, setSyncContactos] = useState(true);
  
  // Logs and conflicts
  const [logs, setLogs] = useState([]);
  const [conflitos, setConflitos] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [logModalOpen, setLogModalOpen] = useState(false);
  
  // Sync progress
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncMessage, setSyncMessage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [configRes, logsRes, conflitosRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sendys/config`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sendys/logs?limit=20`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sendys/conflitos`, { headers })
      ]);
      
      const configData = await configRes.json();
      const logsData = await logsRes.json();
      const conflitosData = await conflitosRes.json();
      
      setConfig(configData);
      setLogs(logsData);
      setConflitos(conflitosData);
      
      // Populate form
      if (configData) {
        setEndpoint(configData.sendys_endpoint || '');
        setFrequencia(configData.frequencia_sync || 'Manual');
        setHoraSync(configData.hora_sync || '02:00');
        setAutoSync(configData.auto_sync_enabled || false);
        setRegraConflito(configData.regra_conflito || 'Mais_Recente');
        setSyncDadosBase(configData.sync_dados_base !== false);
        setSyncValencias(configData.sync_valencias !== false);
        setSyncUtentes(configData.sync_utentes !== false);
        setSyncContactos(configData.sync_contactos !== false);
      }
    } catch (error) {
      console.error('Error loading config:', error);
      toast.error('Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sendys/config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sendys_endpoint: endpoint,
          sendys_api_key: apiKey,
          frequencia_sync: frequencia,
          hora_sync: horaSync,
          auto_sync_enabled: autoSync,
          regra_conflito: regraConflito,
          sync_dados_base: syncDadosBase,
          sync_valencias: syncValencias,
          sync_utentes: syncUtentes,
          sync_contactos: syncContactos
        })
      });
      
      if (response.ok) {
        toast.success('Configuração guardada com sucesso!');
        loadData();
      } else {
        toast.error('Erro ao guardar configuração');
      }
    } catch (error) {
      toast.error('Erro ao guardar configuração');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTestingConnection(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sendys/test-connection`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('Conexão estabelecida com sucesso!');
      } else {
        toast.error(data.message || 'Falha na conexão');
      }
    } catch (error) {
      toast.error('Erro ao testar conexão');
    } finally {
      setTestingConnection(false);
    }
  };

  const executarSync = async (tipo) => {
    setSyncing(true);
    setSyncProgress(0);
    setSyncMessage('A iniciar sincronização...');
    
    try {
      const token = localStorage.getItem('token');
      const endpoint_url = tipo === 'importar' 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/sendys/sync/importar`
        : `${process.env.REACT_APP_BACKEND_URL}/api/sendys/sync/exportar`;
      
      setSyncProgress(30);
      setSyncMessage('A processar dados...');
      
      const response = await fetch(endpoint_url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setSyncProgress(80);
      setSyncMessage('A finalizar...');
      
      const data = await response.json();
      
      setSyncProgress(100);
      
      if (response.ok) {
        toast.success(`Sincronização concluída! ${data.stats?.novos || 0} novos, ${data.stats?.atualizados || data.stats?.exportados || 0} atualizados`);
        loadData();
      } else {
        toast.error(data.detail || 'Erro na sincronização');
      }
    } catch (error) {
      toast.error('Erro na sincronização');
    } finally {
      setSyncing(false);
      setSyncProgress(0);
      setSyncMessage('');
    }
  };

  const resolverConflito = async (conflitoId, decisao) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sendys/conflitos/${conflitoId}/resolver?decisao=${decisao}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Conflito resolvido!');
        loadData();
      } else {
        toast.error('Erro ao resolver conflito');
      }
    } catch (error) {
      toast.error('Erro ao resolver conflito');
    }
  };

  const openLogDetail = (log) => {
    setSelectedLog(log);
    setLogModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="sendys-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sendys-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Database className="w-8 h-8 text-purple-600" />
            Integração Sendys CRM
          </h1>
          <p className="text-slate-500 mt-1">Sincronização bidirecional de dados com o Sendys</p>
        </div>
        {config?.ultima_sync && (
          <Badge variant="outline" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Última sync: {new Date(config.ultima_sync).toLocaleString('pt-PT')}
          </Badge>
        )}
      </div>

      <Tabs defaultValue="config" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="config">Configuração</TabsTrigger>
          <TabsTrigger value="sync">Sincronização</TabsTrigger>
          <TabsTrigger value="logs">Logs</TabsTrigger>
          <TabsTrigger value="conflitos" className="relative">
            Conflitos
            {conflitos.length > 0 && (
              <Badge className="ml-2 bg-red-500">{conflitos.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* CONFIGURAÇÃO */}
        <TabsContent value="config" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Conexão API */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="w-5 h-5 text-slate-500" />
                  Conexão API
                </CardTitle>
                <CardDescription>Configure os dados de acesso ao Sendys CRM</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Endpoint API Sendys</Label>
                  <Input 
                    value={endpoint}
                    onChange={(e) => setEndpoint(e.target.value)}
                    placeholder="https://api.sendys.pt/v1"
                    data-testid="endpoint-input"
                  />
                </div>
                <div>
                  <Label>API Key</Label>
                  <Input 
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="••••••••••••••••"
                    data-testid="apikey-input"
                  />
                </div>
                <Button 
                  variant="outline" 
                  onClick={testConnection}
                  disabled={testingConnection || !endpoint}
                  data-testid="test-connection-btn"
                >
                  {testingConnection ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Zap className="w-4 h-4 mr-2" />
                  )}
                  Testar Conexão
                </Button>
              </CardContent>
            </Card>

            {/* Agendamento */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-slate-500" />
                  Agendamento
                </CardTitle>
                <CardDescription>Configure a sincronização automática</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Frequência</Label>
                  <Select value={frequencia} onValueChange={setFrequencia}>
                    <SelectTrigger data-testid="frequencia-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Manual">Manual</SelectItem>
                      <SelectItem value="Diaria">Diária</SelectItem>
                      <SelectItem value="Semanal">Semanal</SelectItem>
                      <SelectItem value="Mensal">Mensal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Hora da Sincronização</Label>
                  <Input 
                    type="time"
                    value={horaSync}
                    onChange={(e) => setHoraSync(e.target.value)}
                    data-testid="hora-sync-input"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Auto-sync Ativado</Label>
                  <Switch 
                    checked={autoSync}
                    onCheckedChange={setAutoSync}
                    data-testid="auto-sync-switch"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Resolução de Conflitos */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Resolução de Conflitos
                </CardTitle>
                <CardDescription>Como resolver diferenças entre sistemas</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Regra de Conflito</Label>
                  <Select value={regraConflito} onValueChange={setRegraConflito}>
                    <SelectTrigger data-testid="regra-conflito-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Sendys_Prevalece">Sendys Prevalece</SelectItem>
                      <SelectItem value="Entrajudata_Prevalece">Entrajudata Prevalece</SelectItem>
                      <SelectItem value="Mais_Recente">Mais Recente</SelectItem>
                      <SelectItem value="Manual">Resolução Manual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-slate-500">
                  {regraConflito === 'Manual' && '⚠️ Conflitos serão guardados para revisão manual'}
                  {regraConflito === 'Sendys_Prevalece' && 'Dados do Sendys substituem sempre os locais'}
                  {regraConflito === 'Entrajudata_Prevalece' && 'Dados locais são mantidos, Sendys é ignorado'}
                  {regraConflito === 'Mais_Recente' && 'O registo mais recente prevalece'}
                </p>
              </CardContent>
            </Card>

            {/* Dados a Sincronizar */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ArrowLeftRight className="w-5 h-5 text-blue-500" />
                  Dados a Sincronizar
                </CardTitle>
                <CardDescription>Selecione que dados sincronizar</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="sync-base" 
                    checked={syncDadosBase}
                    onCheckedChange={setSyncDadosBase}
                  />
                  <Label htmlFor="sync-base">Dados Base (nome, morada, contactos)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="sync-valencias" 
                    checked={syncValencias}
                    onCheckedChange={setSyncValencias}
                  />
                  <Label htmlFor="sync-valencias">Respostas Sociais</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="sync-utentes" 
                    checked={syncUtentes}
                    onCheckedChange={setSyncUtentes}
                  />
                  <Label htmlFor="sync-utentes">Utentes (contagens)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="sync-contactos" 
                    checked={syncContactos}
                    onCheckedChange={setSyncContactos}
                  />
                  <Label htmlFor="sync-contactos">Contactos Adicionais</Label>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end">
            <Button onClick={saveConfig} disabled={saving} data-testid="save-config-btn">
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Guardar Configuração
            </Button>
          </div>
        </TabsContent>

        {/* SINCRONIZAÇÃO */}
        <TabsContent value="sync" className="space-y-6">
          {/* Progress */}
          {syncing && (
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    <span className="font-medium text-blue-800">{syncMessage}</span>
                  </div>
                  <Progress value={syncProgress} className="h-2" />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Última Sync */}
          {config?.ultima_sync && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Última Sincronização</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <Badge className={STATUS_COLORS[config.status_ultima_sync] || 'bg-slate-500'}>
                    {config.status_ultima_sync || 'N/A'}
                  </Badge>
                  <span className="text-slate-600">
                    {new Date(config.ultima_sync).toLocaleString('pt-PT')}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ações de Sync */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-green-200 hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                    <Download className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Importar</h3>
                    <p className="text-sm text-slate-500">Sendys → Entrajudata</p>
                  </div>
                  <Button 
                    onClick={() => executarSync('importar')}
                    disabled={syncing}
                    className="w-full bg-green-600 hover:bg-green-700"
                    data-testid="sync-importar-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Sincronizar Agora
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-blue-200 hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto">
                    <Upload className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Exportar</h3>
                    <p className="text-sm text-slate-500">Entrajudata → Sendys</p>
                  </div>
                  <Button 
                    onClick={() => executarSync('exportar')}
                    disabled={syncing}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="sync-exportar-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Enviar para Sendys
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-purple-200 hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto">
                    <ArrowLeftRight className="w-8 h-8 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Bidirecional</h3>
                    <p className="text-sm text-slate-500">Sync completo</p>
                  </div>
                  <Button 
                    onClick={async () => {
                      await executarSync('importar');
                      await executarSync('exportar');
                    }}
                    disabled={syncing}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    data-testid="sync-bidirecional-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Sincronização Completa
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Import/Export Excel */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-green-600" />
                Importar/Exportar Excel
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <Button variant="outline">
                  <Upload className="w-4 h-4 mr-2" />
                  Importar Excel
                </Button>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Exportar Excel
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LOGS */}
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5 text-slate-500" />
                Histórico de Sincronizações
              </CardTitle>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  Nenhuma sincronização realizada
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Data/Hora</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-center">Lidos</TableHead>
                      <TableHead className="text-center">Novos</TableHead>
                      <TableHead className="text-center">Atualizados</TableHead>
                      <TableHead className="text-center">Erros</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id} className="hover:bg-slate-50">
                        <TableCell className="text-sm">
                          {new Date(log.data_inicio).toLocaleString('pt-PT')}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{log.tipo}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_COLORS[log.status]}>{log.status}</Badge>
                        </TableCell>
                        <TableCell className="text-center">{log.registos_lidos}</TableCell>
                        <TableCell className="text-center text-green-600 font-medium">
                          {log.registos_novos}
                        </TableCell>
                        <TableCell className="text-center text-blue-600 font-medium">
                          {log.registos_atualizados}
                        </TableCell>
                        <TableCell className="text-center">
                          {log.registos_erros > 0 ? (
                            <Badge className="bg-red-500">{log.registos_erros}</Badge>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => openLogDetail(log)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CONFLITOS */}
        <TabsContent value="conflitos">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Conflitos Pendentes ({conflitos.length})
              </CardTitle>
              <CardDescription>
                Conflitos que requerem resolução manual
              </CardDescription>
            </CardHeader>
            <CardContent>
              {conflitos.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                  <p className="text-slate-500">Nenhum conflito pendente!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {conflitos.map((conflito) => (
                    <Card key={conflito.id} className="border-amber-200">
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{conflito.codigo_ea}</Badge>
                              <span className="font-medium">{conflito.instituicao_nome}</span>
                            </div>
                            <p className="text-sm text-slate-600">
                              Campo: <strong>{conflito.campo}</strong>
                            </p>
                            <div className="grid grid-cols-2 gap-4 mt-2">
                              <div className="bg-blue-50 p-3 rounded-lg">
                                <p className="text-xs text-blue-600 mb-1">Entrajudata</p>
                                <p className="font-medium">{String(conflito.valor_entrajudata)}</p>
                              </div>
                              <div className="bg-purple-50 p-3 rounded-lg">
                                <p className="text-xs text-purple-600 mb-1">Sendys</p>
                                <p className="font-medium">{String(conflito.valor_sendys)}</p>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => resolverConflito(conflito.id, 'entrajudata')}
                              className="text-blue-600"
                            >
                              Manter Entrajudata
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => resolverConflito(conflito.id, 'sendys')}
                              className="text-purple-600"
                            >
                              Usar Sendys
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Log Detail Modal */}
      <Dialog open={logModalOpen} onOpenChange={setLogModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalhes do Log</DialogTitle>
            <DialogDescription>
              {selectedLog?.tipo} - {selectedLog && new Date(selectedLog.data_inicio).toLocaleString('pt-PT')}
            </DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-2xl font-bold">{selectedLog.registos_lidos}</p>
                  <p className="text-xs text-slate-500">Lidos</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{selectedLog.registos_novos}</p>
                  <p className="text-xs text-slate-500">Novos</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{selectedLog.registos_atualizados}</p>
                  <p className="text-xs text-slate-500">Atualizados</p>
                </div>
                <div className="bg-red-50 p-3 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{selectedLog.registos_erros}</p>
                  <p className="text-xs text-slate-500">Erros</p>
                </div>
              </div>
              
              {selectedLog.detalhes_erros?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Erros Detalhados:</h4>
                  <ScrollArea className="h-[200px] border rounded-lg p-2">
                    <pre className="text-xs">
                      {JSON.stringify(selectedLog.detalhes_erros, null, 2)}
                    </pre>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogModalOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
